/**
 * MainProcessCrashListener
 *
 * Listens for main-process crash IPC events and funnels them through
 * crashDedupService, matching the GlobalErrorListener's decision model.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { CrashDialog } from './CrashDialog';
import {
  recordAndDecide,
  recordStance,
  setDialogOpen,
  shouldShowAutoSendToast,
  markAutoSendToastShown,
  buildReportEnrichment,
  signatureFor,
} from '../services/crashDedupService';
import { emitCrashTelemetry } from '../services/crashTelemetry';
import type { CrashContext, CrashDialogAction, CrashReportEnrichment } from '../types/crashTypes';
import { useAppStore } from '../../../store/appStore';
import { toast } from '../../../components/ui/toaster';

export const MainProcessCrashListener = () => {
  const [crashContext, setCrashContext] = useState<CrashContext | null>(null);
  const currentSignatureRef = useRef<string | null>(null);

  const crashReportMode = useAppStore((s) => s.crashReportMode);
  const setCrashReportMode = useAppStore((s) => s.setCrashReportMode);

  const maybeShowAutoSendToast = useCallback(() => {
    if (!shouldShowAutoSendToast()) return;
    markAutoSendToastShown();
    toast.default({
      title: 'Crash report sent automatically',
      description: 'Change in Settings → Privacy.',
      duration: 6000,
    });
  }, []);

  const isTestMode = window.electronAPI?.testMode?.isTestMode?.() === true;

  const sendReport = useCallback(async (context: CrashContext, enrichment?: CrashReportEnrichment) => {
    try {
      await window.electronAPI?.crashDialog?.sendReport(context, enrichment);
    } catch (error) {
      console.error('[MainProcessCrashListener] Failed to send crash report:', error);
    }
  }, []);

  useEffect(() => {
    if (isTestMode) return;
    const api = window.electronAPI?.crashDialog;
    if (!api?.onMainProcessCrash) return;

    const cleanup = api.onMainProcessCrash((context) => {
      const ctx = context as CrashContext;
      const decision = recordAndDecide(ctx, crashReportMode, 'main-process');

      const errorName = 'MainProcessError';
      const dialogShown = decision.kind === 'show_dialog';
      const reportSent = decision.kind === 'silent_send';

      emitCrashTelemetry(
        (name, data) => window.electronAPI?.analytics?.track?.(name, data),
        {
          context: ctx,
          reportMode: crashReportMode,
          surface: 'main',
          source: 'main-process',
          dialogShown,
          reportSent,
          errorName,
        },
      );

      if (decision.kind === 'show_dialog') {
        currentSignatureRef.current = signatureFor(ctx);
        setDialogOpen(true);
        setCrashContext(ctx);
        return;
      }
      if (decision.kind === 'silent_send') {
        void sendReport(ctx, buildReportEnrichment(ctx, crashReportMode, 'silent'));
        if (crashReportMode === 'always') maybeShowAutoSendToast();
        return;
      }
      // silent_drop — no-op.
    });

    return cleanup;
  }, [isTestMode, sendReport, crashReportMode, maybeShowAutoSendToast]);

  const handleActionWithMemory = useCallback(async (result: { action: CrashDialogAction; remember: boolean }) => {
    const signature = currentSignatureRef.current;
    if (result.action === 'send_report') {
      if (crashContext) await sendReport(crashContext, buildReportEnrichment(crashContext, crashReportMode, 'dialog'));
      if (signature) recordStance(signature, 'sent');
      if (result.remember) setCrashReportMode('always');
    } else {
      if (signature) recordStance(signature, 'ignored');
      if (result.remember) setCrashReportMode('never');
    }
    currentSignatureRef.current = null;
    setDialogOpen(false);
    setCrashContext(null);
  }, [crashContext, crashReportMode, sendReport, setCrashReportMode]);

  const defaultContext: CrashContext = {
    workspace: { activeTabType: null, activeTabEngine: null, tabCount: 0 },
    connection: { connectionEngines: [] },
    app: { version: '', platform: 'darwin', arch: '' },
    metrics: { sessionAge: 0, memoryUsageMB: 0 },
    breadcrumbs: [],
    error: { category: 'unknown', message: '' },
    source: 'main',
  };

  return (
    <CrashDialog
      open={!!crashContext}
      context={crashContext ?? defaultContext}
      onAction={() => { /* handled by onActionWithMemory */ }}
      onActionWithMemory={handleActionWithMemory}
    />
  );
};
